import { type Dispatch, useEffect } from "react";

import type { SubmitInfoAction } from "../types";

import { pollJobStatus } from "../../../api/pipeline";

type UseCallJobStatusEffectParams = {
  dispatch: Dispatch<SubmitInfoAction>;
  isSubmitting: boolean;
  jobId: string;
  onJobCompleted: (projectId: string) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
};

export const useCallJobStatusEffect = ({
  dispatch,
  isSubmitting,
  jobId,
  onJobCompleted,
  setIsSubmitting,
}: UseCallJobStatusEffectParams) => {
  useEffect(() => {
    if (!jobId || !isSubmitting) return;

    let inFlight = false;
    const interval = window.setInterval(() => {
      if (inFlight) return;
      inFlight = true;
      void (async () => {
        try {
          dispatch({ type: "UPDATE_PROJECT", updater: (prev) => ({ ...prev, status: "progress" }) });
          const response = await pollJobStatus(jobId);
          const isFailed = response.status === "failed";

          if (isFailed) {
            dispatch({
              type: "UPDATE_PROJECT",
              updater: (prev) => ({
                ...prev,
                errorMessage: response.error,
                percentage: response.progress,
                status: "error",
              }),
            });
            window.clearInterval(interval);
            return;
          }

          dispatch({
            type: "UPDATE_PROJECT",
            updater: (prev) => ({
              ...prev,
              percentage: response.progress,
              status: response.project_id ? "completed" : "progress",
            }),
          });

          if (response.project_id) {
            const projectId = response.project_id;
            setTimeout(() => {
              onJobCompleted(projectId);
              setIsSubmitting(false);
              window.clearInterval(interval);
            }, 1000);
          }
        } finally {
          inFlight = false;
        }
      })();
    }, 5000);

    return () => {
      window.clearInterval(interval);
    };
  }, [dispatch, jobId, isSubmitting, onJobCompleted, setIsSubmitting]);
};
